export const platformContainer = 'container mx-auto px-5 lg:px-0'
export const platformSection = 'scroll-mt-28 py-12 lg:py-14'
export const platformHeroLayout =
    'min-h-[560px] py-10 lg:min-h-[520px] lg:py-12'
export const platformHeroTitleClass =
    'text-[42px] font-extrabold leading-[1.01] tracking-tight text-[#1A1D23] sm:text-[56px] xl:text-[68px]'

export const platformHeroBg = 'bg-[#F4EDE4]'
export const platformAlternateBg = 'bg-[#FFF8F1]'
export const platformNavBg = 'bg-[#f4f3f0]'
export const platformSoftPanelBg = 'bg-[#FFF8EC]'

export const platformCardRadius = 'rounded-2xl'
export const platformCtaRadius = 'rounded-3xl'
export const platformControlRadius = 'rounded-[10px]'
export const platformHeroDescriptionClass =
    'max-w-[560px] text-lg leading-relaxed text-olm-black'
export const platformHeroActionSpacingClass = 'mt-6'

export function PlatformEyebrow({
    children,
    align = 'left',
    variant = 'hero',
    className = '',
}) {
    const colorClass =
        variant === 'section' ? 'text-secondary' : 'text-olm-gray'

    return (
        <p
            className={`mb-[18px] inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${colorClass} ${
                align === 'center' ? 'justify-center' : ''
            } ${className}`}>
            {variant === 'hero' && (
                <span
                    aria-hidden="true"
                    className="h-2 w-2 shrink-0 rounded-full bg-secondary"
                />
            )}
            {children}
        </p>
    )
}

export function PlatformSectionHeading({
    label,
    title,
    accent,
    gradientTitle,
    description,
    align = 'left',
    hero = false,
}) {
    const Tag = hero ? 'h1' : 'h2'
    const highlight = accent || gradientTitle
    const centered = align === 'center'

    return (
        <div
            className={
                centered ? 'mx-auto max-w-[800px] text-center' : undefined
            }>
            {label && (
                <PlatformEyebrow align={align} variant="section">
                    {label}
                </PlatformEyebrow>
            )}
            <Tag
                className={`${description ? 'mb-3' : ''} ${
                    hero
                        ? platformHeroTitleClass
                        : 'text-[32px] font-extrabold leading-[1.06] tracking-tight text-[#1A1D23] lg:text-[42px]'
                }`}>
                {title}
                {highlight && (
                    <>
                        {' '}
                        <span className="bg-rtl-gradient bg-clip-text text-transparent">
                            {highlight}
                        </span>
                    </>
                )}
            </Tag>
            {description && (
                <p
                    className={`text-[17px] leading-relaxed ${
                        centered ? 'mx-auto max-w-[760px]' : 'max-w-[830px]'
                    } text-olm-black`}>
                    {description}
                </p>
            )}
        </div>
    )
}

export function CheckBadge({ className = '' }) {
    return (
        <span
            aria-hidden="true"
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-[10px] font-bold text-olm-green ${className}`}>
            ✓
        </span>
    )
}

export function PlatformCallout({ label, children }) {
    return (
        <aside className="mt-6 rounded-2xl border border-black/10 bg-[#FFF8EC] p-5 shadow-sm sm:p-6">
            <h3 className="mb-2 text-xs font-extrabold uppercase tracking-widest text-secondary">
                {label}
            </h3>
            <p className="text-sm leading-relaxed text-olm-black">{children}</p>
        </aside>
    )
}

export function CheckList({
    items,
    className = '',
    itemClassName = '',
    textClassName = 'text-[13px]',
}) {
    return (
        <ul className={`mt-6 grid gap-3 ${className}`}>
            {items.map((item) => (
                <li
                    key={item}
                    className={`flex items-start gap-3 ${textClassName} font-medium leading-relaxed text-olm-black ${itemClassName}`}>
                    <CheckBadge />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    )
}

export function PlatformProofList({
    items,
    className = '',
    spacingClassName = 'mt-[18px]',
}) {
    return (
        <ul
            className={`${spacingClassName} flex flex-wrap gap-x-4 gap-y-3 text-xs text-olm-gray ${className}`}>
            {items.map((item) => (
                <li key={item} className="flex items-center gap-2">
                    <CheckBadge className="h-[18px] w-[18px]" />
                    {item}
                </li>
            ))}
        </ul>
    )
}

export function PlatformButton({
    label,
    href,
    variant = 'primary',
    full = false,
    className = '',
    onClick,
}) {
    const variantClass =
        variant === 'secondary'
            ? 'border border-black/15 bg-white text-[#1A1D23] hover:border-secondary/30 hover:text-secondary'
            : 'bg-rtl-gradient text-white shadow-md hover:-translate-y-0.5 hover:shadow-lg'

    const classes = `inline-flex items-center justify-center rounded-[10px] px-6 py-3.5 text-sm font-bold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary ${
        full ? 'w-full' : ''
    } ${variantClass} ${className}`

    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={classes}>
                {label}
            </button>
        )
    }

    return (
        <a href={href} className={classes}>
            {label}
        </a>
    )
}

export function FeatureImage({ image, eager = false }) {
    return (
        <img
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            className="block h-auto w-full object-contain"
        />
    )
}

export function PlatformSectionNav({ items, label, useDots = false }) {
    return (
        <nav
            aria-label={label}
            className={`border-y border-black/10 ${platformNavBg}`}>
            <div
                className={`${platformContainer} flex items-center gap-6 overflow-x-auto py-5 lg:justify-between lg:gap-3`}>
                {items.map((item) => (
                    <a
                        key={item.href}
                        href={item.href}
                        className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded text-xs font-bold text-olm-gray hover:text-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                        {item.icon ? (
                            <span aria-hidden="true">{item.icon}</span>
                        ) : useDots ? (
                            <span
                                aria-hidden="true"
                                className="h-[7px] w-[7px] rounded-full border border-secondary/30 bg-[#FFE8D7]"
                            />
                        ) : null}
                        {item.label}
                    </a>
                ))}
            </div>
        </nav>
    )
}

export function PlatformClosingCta({ data, layout = 'split' }) {
    const centered = layout === 'center'
    const action = data.cta || data.action

    return (
        <section className={platformSection}>
            <div className={platformContainer}>
                <div
                    className={`relative overflow-hidden ${platformCtaRadius} bg-gradient-to-br from-[#1A1D23] to-olm-new-black p-7 text-white sm:p-12 ${
                        centered
                            ? 'text-center'
                            : 'lg:flex lg:items-center lg:justify-between lg:gap-8'
                    }`}>
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl"
                    />
                    <div
                        className={`relative ${
                            centered
                                ? 'mx-auto max-w-[760px]'
                                : 'mb-6 lg:mb-0 lg:max-w-[720px]'
                        }`}>
                        <h2 className="mb-3 text-[32px] font-extrabold leading-tight tracking-tight text-white lg:text-[42px]">
                            {data.title}
                        </h2>
                        <p className="text-base leading-relaxed text-gray-300">
                            {data.description}
                        </p>
                    </div>
                    {action && (
                        <div
                            className={`relative shrink-0 ${
                                centered ? 'mt-7' : ''
                            }`}>
                            <PlatformButton {...action} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export function PlatformTrustBar({ items }) {
    return (
        <section
            aria-label="Platform highlights"
            className="border-y border-black/10 bg-[#F9F8F6]">
            <ul
                className={`${platformContainer} flex min-h-[80px] flex-wrap items-center justify-center gap-x-10 gap-y-4 py-5`}>
                {items.map((item) => {
                    const label = typeof item === 'string' ? item : item.label
                    const icon = typeof item === 'string' ? null : item.icon

                    return (
                        <li
                            key={label}
                            className="flex items-center gap-2 text-[11px] font-bold text-olm-black">
                            {icon && (
                                <span
                                    aria-hidden="true"
                                    className="flex h-6 w-6 items-center justify-center rounded-full border border-black/10 bg-white text-secondary">
                                    {icon}
                                </span>
                            )}
                            {label}
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}
